// Owner-facing notification models for the bell/feed/preferences surfaces.
// Wire shapes come from the gateway /api/notifications module (InboxItemDo,
// preference grid) — this is the only boundary that knows both.

import { InjectionToken } from '@angular/core';
import { LUMIR_PRODUCTS, environment, type LumirProduct } from '@environment';

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  linkUrl: string | null;
  product: string | null;
  eventType: string | null;
  isRead: boolean;
  createdAt: string | null;
}

export interface NotificationPage {
  items: NotificationItem[];
  count: number;
  page: number;
  totalPages: number;
}

export interface NotificationPreferenceEvent {
  eventType: string;
  label: string;
  emailEnabled: boolean;
}

export interface NotificationPreferenceProduct {
  product: string;
  label: string;
  events: NotificationPreferenceEvent[];
}

/** `null` keeps the whole grid; a list keeps only its events, products and rows in list order. */
export function pickPreferenceEvents(
  grid: NotificationPreferenceProduct[],
  eventTypes: readonly string[] | null,
): NotificationPreferenceProduct[] {
  if (!eventTypes) return grid;
  const rank = (eventType: string): number => eventTypes.indexOf(eventType);
  return grid
    .map(group => ({
      ...group,
      events: group.events.filter(e => rank(e.eventType) >= 0).sort((a, b) => rank(a.eventType) - rank(b.eventType)),
    }))
    .filter(group => group.events.length > 0)
    .sort((a, b) => rank(a.events[0].eventType) - rank(b.events[0].eventType));
}

// Feed product filter: null = all products.
export type NotificationProductFilter = string | null;

// Product names come from the switcher list, so the filter and the switcher never drift apart.
const switcherLabel = (id: LumirProduct['id']): string => LUMIR_PRODUCTS.find(product => product.id === id)?.label ?? id;

export const NOTIFICATION_PRODUCT_FILTER_LABELS: Record<string, string> = {
  gallery: switcherLabel('gallery'),
  fotoclass: switcherLabel('fotoclass'),
  photobook: switcherLabel('photobook'),
  studio_manager: switcherLabel('studio-manager'),
  other: 'Прочее',
};

// ── cross-product notification navigation ────────────────────────────────────
// Inbox links are app-relative inside their OWN product. The host cabinet's own
// items navigate in-app; a foreign product's relative link composes an absolute
// URL from that product's cabinet origin (empty origin on this stage → the item
// is not actionable). Absolute links open externally only on validated platform
// origins — never an arbitrary URL from the wire.

export type NotificationNavigation = { kind: 'internal'; url: string } | { kind: 'external'; url: string } | null;

/**
 * The inbox `product` tag of the cabinet this bell is mounted in — the one thing the shared
 * notification block cannot know by itself. Its own items stay in-app; every other product's link
 * leaves for that cabinet.
 */
export const NOTIFICATIONS_HOST_PRODUCT = new InjectionToken<string>('NOTIFICATIONS_HOST_PRODUCT');

// Per-product cabinet origin for foreign-product relative links.
const PRODUCT_ORIGIN_BY_INBOX_PRODUCT: Record<string, string> = {
  gallery: environment.GALLERY_APP_URL,
  studio_manager: environment.STUDIO_MANAGER_APP_URL,
  fotoclass: environment.FOTOCLASS_APP_URL,
  photobook: environment.PHOTOBOOK_ENDPOINT,
};

function platformOrigins(): string[] {
  return [
    environment.GALLERY_APP_URL,
    environment.PORTFOLIO_APP_URL,
    environment.FOTOCLASS_APP_URL,
    environment.DASHBOARD_APP_URL,
    environment.STUDIO_MANAGER_APP_URL,
    environment.PHOTOBOOK_ENDPOINT,
    environment.PORTFOLIO_CABINET_ORIGIN,
    environment.FOTOCLASS_ORIGIN,
    environment.GALLERY_PUBLIC_ORIGIN,
    environment.PORTFOLIO_PUBLIC_ORIGIN,
    environment.STUDIO_PUBLIC_ENDPOINT,
  ].filter(origin => origin !== '');
}

export function resolveNotificationNavigation(
  item: Pick<NotificationItem, 'product' | 'linkUrl'>,
  hostProduct: string,
): NotificationNavigation {
  const url = item.linkUrl;
  if (!url) return null;

  if (/^https?:\/\//i.test(url)) {
    try {
      const target = new URL(url);
      const allowed = platformOrigins().some(origin => new URL(origin).origin === target.origin);
      return allowed ? { kind: 'external', url } : null;
    } catch {
      return null;
    }
  }

  if (!url.startsWith('/')) return null;

  // Relative link: this cabinet's own items (and legacy items without a product
  // tag) stay in-app; a foreign product needs its cabinet origin.
  if (item.product === null || item.product === hostProduct) return { kind: 'internal', url };
  const base = PRODUCT_ORIGIN_BY_INBOX_PRODUCT[item.product] ?? '';
  if (base === '') return null;
  return { kind: 'external', url: `${base.replace(/\/+$/, '')}${url}` };
}
